package fr.frux.core.ads

import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.window.DialogProperties
import fr.frux.core.ads.resources.Res
import fr.frux.core.ads.resources.ads_intro_body
import fr.frux.core.ads.resources.ads_intro_continue
import fr.frux.core.ads.resources.ads_intro_learn_more
import fr.frux.core.ads.resources.ads_intro_learn_more_description
import fr.frux.core.ads.resources.ads_intro_title
import fr.frux.core.common.config.SiteLinks
import org.jetbrains.compose.resources.stringResource

/**
 * Affiche l'écran d'information « Publicité dans Frux » quand le contrôleur l'attend
 * ([AdConsent.IntroPending]), et lui transmet la réponse (#527). À poser une fois, par-dessus
 * l'application, jamais sous un écran de démarrage qui le masquerait.
 */
@Composable
fun AdsConsentIntroHost(controller: AdsController = LocalAdsController.current) {
    val state by controller.state.collectAsState()
    if (state.consent == AdConsent.IntroPending) {
        AdsConsentIntroDialog(
            onContinue = controller::onConsentIntroContinue,
            onDismiss = controller::onConsentIntroDismissed,
        )
    }
}

/**
 * Écran d'information qui précède le formulaire de consentement UMP (ADR 0030, amendement #526).
 * « Continuer » ouvre le formulaire. Le retour arrière l'écarte jusqu'à la session suivante. Un
 * toucher hors du dialogue ne l'écarte pas : refuser se fait dans le formulaire, au même niveau
 * qu'accepter, pas par un geste accidentel. « En savoir plus » ouvre la section publicité de la
 * politique de confidentialité sans fermer le dialogue.
 */
@Composable
fun AdsConsentIntroDialog(
    onContinue: () -> Unit,
    onDismiss: () -> Unit,
) {
    val uriHandler = LocalUriHandler.current
    val learnMoreDescription = stringResource(Res.string.ads_intro_learn_more_description)
    AlertDialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(dismissOnBackPress = true, dismissOnClickOutside = false),
        title = {
            Text(
                text = stringResource(Res.string.ads_intro_title),
                modifier = Modifier.semantics { heading() },
            )
        },
        text = { Text(stringResource(Res.string.ads_intro_body)) },
        confirmButton = {
            Button(onClick = onContinue) { Text(stringResource(Res.string.ads_intro_continue)) }
        },
        dismissButton = {
            TextButton(
                onClick = { uriHandler.openUri(SiteLinks.PRIVACY_ADS) },
                modifier = Modifier.semantics { contentDescription = learnMoreDescription },
            ) {
                Text(stringResource(Res.string.ads_intro_learn_more))
            }
        },
    )
}

/**
 * Suspend toute publicité (et tout appel à UMP ou à la régie pour [AdHold.ONBOARDING]) tant que
 * [active] est vrai et que l'appelant est composé. Le hold est posé à l'application de la
 * composition, avant que le contrôleur ne reprenne la main sur le thread principal.
 */
@Composable
fun HoldAds(
    reason: AdHold,
    active: Boolean,
    controller: AdsController = LocalAdsController.current,
) {
    DisposableEffect(controller, reason, active) {
        val handle = if (active) controller.hold(reason) else null
        onDispose { handle?.release() }
    }
}
